import React, {useState, useEffect} from 'react';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import Header from "../common/Header";
import StrapiService from "../strapi/StrapiService";
import {buttonStyle, inputStyle} from "../styles";
import {BASIC_URL} from "../enum";
import {useNavigate} from "react-router-dom";

const EditProfile = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    salutation: '',
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    newPassword: '',
    confirmPassword: '',
    newsletterAgreed: false,
  });

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    StrapiService.getCurrentUser().then(user => {
      setFormData((prev) => ({
        ...prev,
        salutation: user.Anrede || '',
        firstName: user.Vorname || '',
        lastName: user.Nachname || '',
        email: user.email || '',
        companyName: user.Unternehmen || '',
        newsletterAgreed: user.Newsletter_zugestimmt ?? false,
      }));
    })
      .catch(() => {
        setError(t('edit_profile.error.fetch'))
        localStorage.removeItem('jwt');
        localStorage.removeItem('user');
        navigate('/login');
      })
  }, []);

  const handleChange = (e) => {
    const {name, value, type, checked} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError('');

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setError(t('edit_profile.error.passwordMismatch'));
      return;
    }

    try {
      setLoading(true);
      const user = await StrapiService.getCurrentUser();

      if (!user || !user.id) {
        throw new Error('User not available or missing ID');
      }

      const token = localStorage.getItem('jwt');
      const payload = {
        Vorname: formData.firstName,
        Nachname: formData.lastName,
        email: formData.email,
        Anrede: formData.salutation,
        Newsletter_zugestimmt: formData.newsletterAgreed,
      };

      if (formData.newPassword) {
        payload.password = formData.newPassword;
      }

      await axios.put(`${BASIC_URL}/api/users/${user.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(t('edit_profile.error.submit'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ pb: 3 }}>
        <Box mt={4} mb={3}>
          <Typography variant="h4" align="center" gutterBottom>
            {t('edit_profile.title')}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit} noValidate>
          <FormControl fullWidth margin="normal" sx={{backgroundColor: '#f7f8fa'}}>
            <InputLabel sx={{backgroundColor: '#f7f8fa', px: 0.5}}>{t('edit_profile.salutation')}</InputLabel>
            <Select
              name="salutation"
              value={formData.salutation}
              onChange={handleChange}
              sx={inputStyle}
            >
              <MenuItem value="">
                <em>{t('edit_profile.select_placeholder')}</em>
              </MenuItem>
              <MenuItem value="Herr">{t('edit_profile.salutation_herr')}</MenuItem>
              <MenuItem value="Frau">{t('edit_profile.salutation_frau')}</MenuItem>
              <MenuItem value="Divers">{t('edit_profile.salutation_divers')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label={t('edit_profile.firstName')}
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            sx={inputStyle}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('edit_profile.lastName')}
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            sx={inputStyle}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('edit_profile.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            sx={inputStyle}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('edit_profile.company')}
            name="companyName"
            value={formData.companyName}
            disabled
            sx={inputStyle}
          />

          <TextField
            fullWidth
            margin="normal"
            label={t('edit_profile.newPassword')}
            name="newPassword"
            type="password"
            value={formData.newPassword}
            onChange={handleChange}
            sx={inputStyle}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('edit_profile.confirmPassword')}
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            sx={inputStyle}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.newsletterAgreed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    newsletterAgreed: e.target.checked,
                  }))
                }
                sx={{
                  color: '#0063a6',
                  '&.Mui-checked': {
                    color: '#0063a6',
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.5,
                }}
              >
                {t('edit_profile.newsletter')}
              </Typography>
            }
            sx={{
              alignItems: 'center',
              mb: 2,
              display: 'flex',
              flexDirection: 'row',
            }}
          />

          <Box mt={3} position="relative">
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={buttonStyle}
            >
              {t('edit_profile.submit')}
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{position: 'absolute', top: '50%', right: '16px', marginTop: '-12px'}}
              />
            )}
          </Box>
        </form>

        {success && (
          <Box mt={2}>
            <Alert severity="success">{t('edit_profile.success')}</Alert>
          </Box>
        )}
        {error && (
          <Box mt={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
      </Container>
    </>
  );
};

export default EditProfile;
